import { forwardRef, useCallback } from 'react'

import { GameId } from '@common'
import { StoreRewardItem } from '@common/models/bonus'
import cx from 'classnames'
import Image, { getImageProps } from 'next/image'
import { useTranslations } from 'next-intl'

import BonusTrackPointsIcon from '@/assets/images/storefronts/bonus-track-points-icon.svg'
import BsCheckMark from '@/assets/images/storefronts/brawlstars/bonus-item-checkmark.png'
import BrawlFlare from '@/assets/images/storefronts/brawlstars/rays.png'
import CocCheckMark from '@/assets/images/storefronts/clashofclans/bonus-item-checkmark.png'
import CrCheckMark from '@/assets/images/storefronts/clashroyale/bonus-item-checkmark.png'
import { ButtonProps, Button } from '@/components/buttons/Button'
import { useClaimBonus } from '@/features/common/useClaimBonus'
import { useFormattedAmount } from '@/features/storefronts/_storeBase/card/utils'
import { getBackgroundImage } from '@/features/storefronts/_storeBase/imageWithFallback/imageUtils'
import { StoreTypography } from '@/features/storefronts/_storeBase/typography/StoreTypography'
import { useGameSlug } from '@/features/storefronts/game-provider'
import { useTranslatable } from '@/localization/useTranslatable'
import { useAppSelector } from '@/utils/useAppSelector'

import { BonusItemImage } from '../../shared/BonusItemImage'
import { ProgressBar } from '../../shared/ProgressBar'
import { BonusItemStatus } from '../../types'

import styles from './bonusTrackItem.module.scss'

const ClaimButton: React.FC<Omit<ButtonProps, 'onClick'> & { item: StoreRewardItem }> = ({ item, type, ...props }) => {
  const t = useTranslations()
  const { claimBonus, isLoading } = useClaimBonus()

  const onClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      await claimBonus(item)
    },
    [item, claimBonus],
  )
  return (
    <Button
      {...props}
      type={type}
      onClick={onClick}
      data-type="claim"
      additionalClasses={cx(styles.claimButton)}
      loading={isLoading}
      disabled={isLoading}
    >
      <StoreTypography
        tag="span"
        style="plain"
        textSize="sm"
        overrideFont="supercellHeadline"
        className={styles.btnText}
      >
        {t('bonus_claim')}
      </StoreTypography>
    </Button>
  )
}

const getCheckMarkImage = (gameSlug: GameId) => {
  if (gameSlug === 'brawlstars') {
    return BsCheckMark
  }

  if (gameSlug === 'squadbusters') {
    return BsCheckMark
  }

  if (gameSlug === 'clashroyale') {
    return CrCheckMark
  }

  return CocCheckMark
}

interface BonusTrackItemProps {
  balance: number
  item: StoreRewardItem
  status: BonusItemStatus
  progressPercentage: number
  lastItem?: boolean
}

export const BonusTrackItem = forwardRef<HTMLDivElement, BonusTrackItemProps>(
  ({ balance, item, status, progressPercentage, lastItem = false }, ref) => {
    const t = useTranslations()
    const gameSlug = useGameSlug()
    const translatable = useTranslatable()

    const claimedBonusItems = useAppSelector((state) => state.common.claimedBonusItems)
    const mode = claimedBonusItems.includes(item.id) ? 'claimed' : status

    const isClaimedMode = mode === 'claimed'
    const isUnclaimedMode = mode === 'unclaimed'
    const isNextMode = mode === 'next'
    const isUpcomingMode = mode === 'upcoming'

    const showButton = isUnclaimedMode && !item.claimed

    const remainingPoints = item.cost - balance
    const remainingPointsValue = useFormattedAmount(remainingPoints)

    const {
      props: { srcSet },
    } = getImageProps({ alt: '', width: 400, height: 400, src: BrawlFlare })
    const backgroundImage = getBackgroundImage(srcSet)

    return (
      <div
        className={cx(styles.item, styles[mode], styles[gameSlug], {
          [styles.lastItem]: lastItem,
        })}
        ref={ref}
      >
        <div className={styles.content}>
          {isClaimedMode && (
            <div className={styles.claimedIconContainer}>
              <Image src={getCheckMarkImage(gameSlug)} alt={t('bonus_claimed')} width={50} height={50} />
            </div>
          )}
          <div className={styles.detailsContainer}>
            <div className={styles.textGroup}>
              {(isClaimedMode || isUnclaimedMode) && (
                <>
                  <StoreTypography
                    className={styles.title}
                    tag="span"
                    textSize="body"
                    overrideFont="supercellHeadline"
                    style="plain"
                  >
                    {translatable(item.title, 'title')}
                  </StoreTypography>

                  <StoreTypography
                    className={styles.claimStatus}
                    tag="span"
                    textSize="sm"
                    style="plain"
                    overrideFont="supercellText"
                  >
                    {t(isClaimedMode ? 'bonus_claimed' : 'available_for_claiming')}
                  </StoreTypography>
                </>
              )}

              {(isNextMode || isUpcomingMode) && (
                <>
                  <div className={styles.pointsWrapper}>
                    <Image src={BonusTrackPointsIcon} width="24" height="16" alt="store bonus points" />

                    <StoreTypography
                      className={styles.points}
                      tag="span"
                      textSize="body"
                      style="plain"
                      overrideFont="supercellHeadline"
                    >
                      {remainingPointsValue}
                    </StoreTypography>

                    <StoreTypography tag="span" textSize="xs" style="plain" overrideFont="supercellHeadline">
                      {t('points')}
                    </StoreTypography>
                  </div>

                  <StoreTypography
                    className={styles.bonusPrize}
                    tag="span"
                    textSize="sm"
                    style="plain"
                    overrideFont="supercellText"
                  >
                    {t('to_variable', { n: translatable(item.title, 'title') })}
                  </StoreTypography>
                </>
              )}
            </div>

            {isNextMode && <ProgressBar progressPercentage={progressPercentage} />}
          </div>

          <div className={styles.bonusItemContainer}>
            <div className={styles.imageContainer}>
              <div className={styles.flareContainer} style={{ backgroundImage: backgroundImage }} />

              <BonusItemImage item={item} status={mode} />
            </div>

            {showButton && <ClaimButton content={t('bonus_claim')} item={item} type="Primary" />}
          </div>
        </div>
      </div>
    )
  },
)

BonusTrackItem.displayName = 'BonusTrackItem'
